/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

// @mui material components
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Grid from '@mui/material/Grid';
import TableHead from '@mui/material/TableHead';
import TableContainer from '@mui/material/TableContainer';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import SearchRawmat from './SearchRawmat';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

function BillOfMaterialList() {
  const mod = 'bom_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const calculateTotalCost = () => {
    return record.reduce((total, row) => {
      const cost = parseFloat(row.cost) || 0;
      const quantity = parseFloat(row.quantity) || 0;
      return total + cost * quantity;
    }, 0);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
      }
      setLoading(false);
    });
  };

  const add = (row) => {
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      bom_id: id,
      barcode_id: row.barcode_id,
      quantity: row.quantity,
      unit_id: row.unit_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const edit = (item) => {
    FetchData('PATCH', `/${mod}/${item.id}`, app.token, {
      quantity: item.quantity,
      quantity_balance: item.quantity,
    }).then((result) => {
      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view(find);
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const btnDel = (item) => {
    return (
      <MDButton variant='text' color='error' size='small' iconOnly onClick={() => del(item)}>
        <Icon>delete</Icon>
      </MDButton>
    );
  };

  const renderHeaderEdit = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell align='center' width={10}>
            No.
          </TableCell>
          <TableCell align='center' width={40}>
            รูป
          </TableCell>
          <TableCell>Barcode</TableCell>
          <TableCell>สินค้า</TableCell>
          <TableCell>ต้นทุน</TableCell>
          <TableCell align='right' width={150}>
            จำนวน
          </TableCell>
          <TableCell>หน่วย</TableCell>
          <TableCell width={10}>&nbsp;</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderRecordEdit = () => {
    const totalCost = calculateTotalCost();

    return (
      <TableBody>
        {record.map((row) => (
          <TableRow key={row.id}>
            <TableCell align='center'>{row.no}</TableCell>
            <TableCell>{Func.imageDisplay(row)}</TableCell>
            <TableCell align='left'>{row.barcode}</TableCell>
            <TableCell>
              <MDBox>{row.code}</MDBox>
              <MDBox>{row.name}</MDBox>
            </TableCell>
            <TableCell>{Func.numberDisplay(row.cost) || 0}</TableCell>
            <TableCell align='right'>{row.quantity}</TableCell>
            <TableCell align='left'>{row.unit_name}</TableCell>
            <TableCell align='center'>{btnDel(row)}</TableCell>
          </TableRow>
        ))}
        {record.length > 0 && (
          <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
            <TableCell colSpan={4} align='right' sx={{ fontWeight: 'bold' }}>
              รวมต้นทุนทั้งหมด:
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {Func.numberDisplay(totalCost)}
            </TableCell>
            <TableCell colSpan={3}>&nbsp;</TableCell>
          </TableRow>
        )}
      </TableBody>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{ textAlign: 'right' }}>
            <MDBox p={2}>
              <SearchRawmat add={add} />
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 'none', minHeight: 500 }}>
              <Table aria-label='simple table'>
                {renderHeaderEdit()}
                {renderRecordEdit()}
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view(find);
  }, [id]);

  return (
    <div>
      {renderList()}
      {notify}
    </div>
  );
}

export default BillOfMaterialList;
